import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminProfesoresPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const ProfesorSkeleton = () => (
  <div className="APP-skeleton">
    <div className="APP-skeleton-image" />
    <div className="APP-skeleton-body">
      <div className="APP-skeleton-line APP-skeleton-line--short" />
      <div className="APP-skeleton-line APP-skeleton-line--medium" />
      <div className="APP-skeleton-line APP-skeleton-line--long" />
    </div>
  </div>
);

const EmptyState = ({ onCreate }) => (
  <div className="APP-empty-state">
    <i className="bi-person-badge APP-empty-icon" />
    <h5 className="APP-empty-title">Aún no hay profesores</h5>
    <p className="APP-empty-description">
      Crea tu primer profesor para comenzar a gestionar las asesorías.
    </p>
    {onCreate && (
      <Button variant="primary" onClick={onCreate}>
        <i className="bi-plus-circle me-2" />
        Crear profesor
      </Button>
    )}
  </div>
);

const AdminProfesoresPageInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  // Verificar permisos
  const hasViewProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_PROFESORES), [user]);
  const hasCreateProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.CREAR_PROFESORES), [user]);
  const hasEditProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_PROFESORES), [user]);
  const hasDeleteProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.ELIMINAR_PROFESORES), [user]);

  // Si no tiene permiso para ver profesores, redirigir
  useEffect(() => {
    if (user && !hasViewProfesores) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de profesores.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewProfesores, navigate, notify]);

  const [profesores, setProfesores] = useState([]);
  const [search, setSearch] = useState('');
  const [filterEspecialidad, setFilterEspecialidad] = useState('');
  const [loadingProfesores, setLoadingProfesores] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createProfesorData, setCreateProfesorData] = useState({
    Nombre: '',
    apellido: '',
    Especialidad: '',
    Descripcion: '',
    Foto: '',
  });
  const [creatingProfesor, setCreatingProfesor] = useState(false);

  const fetchProfesores = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingProfesores(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterEspecialidad) params.append('especialidad', filterEspecialidad);

      const response = await fetch(buildApiUrl(`/api/profesores?${params.toString()}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar profesores (${response.status})`);
      }
      const data = await response.json();
      setProfesores(data.profesores || []);
    } catch (error) {
      logError('[AdminProfesoresPage] Error fetching profesores:', error);
      notify({
        title: 'No pudimos cargar los profesores',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingProfesores(false);
    }
  };

  useEffect(() => {
    fetchProfesores();
  }, [token, search, filterEspecialidad]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchProfesores(true);
    setIsRefreshing(false);
    notify({
      title: 'Datos actualizados',
      message: 'Se actualizaron los profesores exitosamente.',
      variant: 'success',
    });
  };

  const handleCreateProfesor = async () => {
    if (!createProfesorData.Nombre || !createProfesorData.apellido) {
      notify({
        title: 'Campos requeridos',
        message: 'Nombre y Apellido son obligatorios',
        variant: 'warning',
      });
      return;
    }

    setCreatingProfesor(true);
    try {
      const response = await fetch(buildApiUrl('/api/profesores'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(createProfesorData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear profesor');
      }

      notify({
        title: 'Profesor creado',
        message: 'El profesor se creó exitosamente.',
        variant: 'success',
      });

      setShowCreateModal(false);
      setCreateProfesorData({
        Nombre: '',
        apellido: '',
        Especialidad: '',
        Descripcion: '',
        Foto: '',
      });
      await fetchProfesores(true);
    } catch (error) {
      notify({
        title: 'Error al crear profesor',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setCreatingProfesor(false);
    }
  };

  const handleDeleteProfesor = async (profesorId, profesorName) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar al profesor "${profesorName}"?`)) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/profesores/${profesorId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al eliminar profesor');
      }

      notify({
        title: 'Éxito',
        message: 'Profesor eliminado exitosamente.',
        variant: 'success',
      });

      await fetchProfesores(true);
    } catch (error) {
      logError('Error eliminando profesor:', error);
      notify({
        title: 'Error',
        message: 'No se pudo eliminar el profesor.',
        variant: 'error',
      });
    }
  };

  // Obtener especialidades únicas para el filtro
  const especialidades = useMemo(() => {
    const unique = [...new Set(profesores.map(p => p.Especialidad).filter(Boolean))];
    return unique.sort();
  }, [profesores]);

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar activeKey="profesores" onNavigate={(item) => navigate(item.path)} />
        <main className="admin-main">
          <div className="APP-container">
            {/* Header */}
            <div className="APP-header">
              <div>
                <h1 className="APP-title">
                  <i className="bi-person-badge" />
                  Profesores
                </h1>
                <p className="APP-subtitle">Gestiona los profesores disponibles para asesorías</p>
              </div>
              <div className="APP-header-actions">
                <Button
                  variant="outline-secondary"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                  <i className={`bi-arrow-repeat ${isRefreshing ? 'spinning' : ''}`} />
                </Button>
                {hasCreateProfesores && (
                  <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                    <i className="bi-plus-circle me-2" />
                    Crear Profesor
                  </Button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="APP-filters">
              <Form.Control
                type="text"
                placeholder="Buscar profesores..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="APP-search-input"
              />
              <Form.Select
                value={filterEspecialidad}
                onChange={(e) => setFilterEspecialidad(e.target.value)}
                className="APP-filter-select"
              >
                <option value="">Todas las especialidades</option>
                {especialidades.map((esp) => (
                  <option key={esp} value={esp}>
                    {esp}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Profesores List */}
            <div className="APP-content">
              {loadingProfesores ? (
                <div className="APP-skeletons">
                  {[...Array(6)].map((_, i) => (
                    <ProfesorSkeleton key={i} />
                  ))}
                </div>
              ) : profesores.length === 0 ? (
                <EmptyState onCreate={hasCreateProfesores ? () => setShowCreateModal(true) : null} />
              ) : (
                <div className="APP-profesores-grid">
                  {profesores.map((profesor) => (
                    <div
                      key={profesor.id}
                      className="APP-profesor-card"
                      onClick={() => navigate(`/admin/profesores/${profesor.id}`)}
                    >
                      <div className="APP-profesor-image-wrapper">
                        {profesor.Foto ? (
                          <img
                            src={resolveImageUrl(profesor.Foto)}
                            alt={`${profesor.Nombre} ${profesor.apellido}`}
                            className="APP-profesor-image"
                          />
                        ) : (
                          <div className="APP-profesor-image-placeholder">
                            <i className="bi-person-badge" />
                          </div>
                        )}
                      </div>
                      <div className="APP-profesor-body">
                        <div className="APP-profesor-header">
                          <h3 className="APP-profesor-name">
                            {profesor.Nombre} {profesor.apellido}
                          </h3>
                          {profesor.Especialidad && (
                            <Badge bg="primary" className="APP-profesor-specialty">
                              {profesor.Especialidad}
                            </Badge>
                          )}
                        </div>
                        {profesor.Descripcion && (
                          <p className="APP-profesor-description">
                            {profesor.Descripcion.substring(0, 150)}
                            {profesor.Descripcion.length > 150 ? '...' : ''}
                          </p>
                        )}
                        <div className="APP-profesor-actions">
                          {hasEditProfesores && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/profesores/${profesor.id}`);
                              }}
                            >
                              <i className="bi-pencil me-1" />
                              Editar
                            </Button>
                          )}
                          {hasDeleteProfesores && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProfesor(profesor.id, `${profesor.Nombre} ${profesor.apellido}`);
                              }}
                            >
                              <i className="bi-trash me-1" />
                              Eliminar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Crear Nuevo Profesor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                type="text"
                value={createProfesorData.Nombre}
                onChange={(e) =>
                  setCreateProfesorData({ ...createProfesorData, Nombre: e.target.value })
                }
                placeholder="Nombre del profesor"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Apellido *</Form.Label>
              <Form.Control
                type="text"
                value={createProfesorData.apellido}
                onChange={(e) =>
                  setCreateProfesorData({ ...createProfesorData, apellido: e.target.value })
                }
                placeholder="Apellido del profesor"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Especialidad</Form.Label>
              <Form.Control
                type="text"
                value={createProfesorData.Especialidad}
                onChange={(e) =>
                  setCreateProfesorData({ ...createProfesorData, Especialidad: e.target.value })
                }
                placeholder="Ej: Odontología, Ortodoncia, etc."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={createProfesorData.Descripcion}
                onChange={(e) =>
                  setCreateProfesorData({ ...createProfesorData, Descripcion: e.target.value })
                }
                placeholder="Descripción del profesor..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>URL de Foto</Form.Label>
              <Form.Control
                type="url"
                value={createProfesorData.Foto}
                onChange={(e) =>
                  setCreateProfesorData({ ...createProfesorData, Foto: e.target.value })
                }
                placeholder="https://ejemplo.com/foto.jpg"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateProfesor}
            disabled={creatingProfesor || !createProfesorData.Nombre || !createProfesorData.apellido}
          >
            {creatingProfesor ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creando...
              </>
            ) : (
              <>
                <i className="bi-check-circle me-2" />
                Crear Profesor
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminProfesoresPage = () => (
  <AdminNotificationsProvider>
    <AdminProfesoresPageInner />
  </AdminNotificationsProvider>
);

export default AdminProfesoresPage;

